import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Play, Pause, Sparkle, MusicNotes, TrashSimple, FilmSlate, ShareNetwork, Warning, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { fileUrl } from "../lib/api";
import PremiumGate from "../components/PremiumGate";

const inp = "w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-cream placeholder:text-cream/30 focus:outline-none focus:border-papaya text-sm";
const Chip = ({ active, onClick, children, testid, disabled }) => (
  <button type="button" disabled={disabled} onClick={onClick} data-testid={testid} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.15em] uppercase border ${active ? "bg-papaya text-ink border-papaya" : "bg-white/5 text-cream/70 border-white/10"} disabled:opacity-50`}>{children}</button>
);

/** AI Song Studio — title / lyrics / style / mood / tempo → ElevenLabs Music → song library → post it or dance to it. */
export default function AISongStudio() {
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({ title: "", lyrics: "", style: "soca", mood: "hype", tempo: "fast", vocal: "female", language: "English", duration_sec: 30, topic: "" });
  const [songs, setSongs] = useState([]);
  const [busy, setBusy] = useState("");
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [{ data: m }, { data: list }] = await Promise.all([api.get("/ai/song/status"), api.get("/ai/song")]);
      setMeta(m); setSongs(list);
    } catch { toast.error("Could not load AI Song Studio"); }
  }, []);
  useEffect(() => { load(); return () => { audioRef.current?.pause(); clearInterval(pollRef.current); }; }, [load]);

  // Poll while any song is generating
  useEffect(() => {
    const pending = songs.filter((s) => s.status === "generating");
    clearInterval(pollRef.current);
    if (pending.length === 0) return;
    pollRef.current = setInterval(async () => {
      const updates = await Promise.all(pending.map((s) => api.get(`/ai/song/${s.id}`).then((r) => r.data).catch(() => null)));
      setSongs((prev) => prev.map((s) => updates.find((u) => u && u.id === s.id) || s));
      updates.forEach((u) => {
        if (u?.status === "ready") toast.success(`“${u.title}” is ready 🎵`);
        if (u?.status === "failed") toast.error(u.error || "Song generation failed");
      });
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [songs]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const writeLyrics = async () => {
    setBusy("lyrics");
    try {
      const { data } = await api.post("/ai/song/lyrics", { title: form.title, style: form.style, mood: form.mood, topic: form.topic, language: form.language });
      set("lyrics", data.lyrics);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI lyrics failed"); }
    finally { setBusy(""); }
  };

  const generate = async () => {
    if (!form.title.trim()) return toast.error("Give your song a title");
    setBusy("generate");
    try {
      const { data } = await api.post("/ai/song/generate", { ...form, duration_sec: Number(form.duration_sec) });
      setSongs((s) => [data, ...s]);
      setMeta((m) => m && ({ ...m, usage: { songs_today: m.usage.songs_today + 1 } }));
      toast.success("Composing your song — usually 30–90 seconds");
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    finally { setBusy(""); }
  };

  const play = (s) => {
    if (playing === s.id) { audioRef.current?.pause(); setPlaying(null); return; }
    audioRef.current?.pause();
    const a = new Audio(fileUrl(s.url));
    a.onended = () => setPlaying(null);
    a.play().catch(() => toast.error("Playback blocked — tap again"));
    audioRef.current = a; setPlaying(s.id);
  };

  const publish = async (s) => {
    try {
      const { data } = await api.post(`/ai/song/${s.id}/publish`);
      setSongs((list) => list.map((x) => (x.id === s.id ? { ...x, sound_id: data.id } : x)));
      toast.success(data.already_existed ? "Already in the Music picker" : "Added to the Music picker — anyone can dance to it now");
      return data.id;
    } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); return null; }
  };

  const makeVideo = async (s) => {
    const sid = s.sound_id || (await publish(s));
    if (sid) nav(`/create?music=${sid}`);
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete “${s.title}”?`)) return;
    try { await api.delete(`/ai/song/${s.id}`); setSongs((l) => l.filter((x) => x.id !== s.id)); } catch { toast.error("Delete failed"); }
  };

  const left = meta ? Math.max(0, meta.limits.songs_per_day - meta.usage.songs_today) : 0;
  const maxSec = meta?.limits?.max_seconds || 60;

  return (
    <div className="min-h-[100dvh] bg-ink pb-32 px-5 pt-6" data-testid="ai-song-studio">
      <div className="flex items-center gap-3">
        <button onClick={() => nav(-1)} className="w-10 h-10 rounded-full crystal-glass flex items-center justify-center text-cream" data-testid="aisong-back"><ArrowLeft size={18} /></button>
        <div className="flex-1">
          <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Creator Studio · Eleven Music</p>
          <h1 className="text-cream text-3xl font-black tracking-tighter">AI Song Studio</h1>
        </div>
        {meta && <span className="text-cream/50 text-[11px]" data-testid="aisong-usage">{left} of {meta.limits.songs_per_day} songs left today</span>}
      </div>

      {meta && <div className="mt-5"><PremiumGate used={meta.usage.songs_today} limit={meta.limits.songs_per_day} unit="songs today" isPremium={meta.is_premium} testid="aisong-gate" /></div>}

      <section className="mt-6 space-y-4">
        <input value={form.title} onChange={(e) => set("title", e.target.value.slice(0, 80))} placeholder="Song title — e.g. Wine Pon Di Riddim" data-testid="aisong-title" className={inp} />
        <div>
          <p className="text-cream/60 text-[10px] tracking-[0.2em] uppercase font-bold mb-1.5">Style</p>
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="aisong-styles">{(meta?.styles || []).map((s) => <Chip key={s} active={form.style === s} onClick={() => set("style", s)} testid={`aisong-style-${s}`}>{s}</Chip>)}</div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-cream/60 text-[10px] tracking-[0.2em] uppercase font-bold mb-1.5">Mood</p>
            <div className="flex gap-1.5 flex-wrap" data-testid="aisong-moods">{(meta?.moods || []).map((m) => <Chip key={m} active={form.mood === m} onClick={() => set("mood", m)} testid={`aisong-mood-${m}`}>{m}</Chip>)}</div>
          </div>
          <div>
            <p className="text-cream/60 text-[10px] tracking-[0.2em] uppercase font-bold mb-1.5">Tempo</p>
            <div className="flex gap-1.5 flex-wrap" data-testid="aisong-tempos">{(meta?.tempos || []).map((t) => <Chip key={t} active={form.tempo === t} onClick={() => set("tempo", t)} testid={`aisong-tempo-${t}`}>{t}</Chip>)}</div>
            <p className="text-cream/60 text-[10px] tracking-[0.2em] uppercase font-bold mb-1.5 mt-3">Vocals</p>
            <div className="flex gap-1.5 flex-wrap" data-testid="aisong-vocals">{(meta?.vocals || []).map((v) => <Chip key={v} active={form.vocal === v} onClick={() => set("vocal", v)} testid={`aisong-vocal-${v}`}>{v}</Chip>)}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-cream/60 text-[10px] tracking-[0.2em] uppercase font-bold">Language</span>
            <input value={form.language} onChange={(e) => set("language", e.target.value.slice(0, 40))} data-testid="aisong-language" className={`${inp} mt-1`} />
          </label>
          <label className="block">
            <span className="text-cream/60 text-[10px] tracking-[0.2em] uppercase font-bold">Length · {form.duration_sec}s (max {maxSec}s)</span>
            <input type="range" min="10" max={maxSec} step="5" value={Math.min(form.duration_sec, maxSec)} onChange={(e) => set("duration_sec", Number(e.target.value))} data-testid="aisong-duration" className="w-full accent-[#38BDF8] mt-3" />
          </label>
        </div>
        {form.vocal !== "instrumental" && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-cream/60 text-[10px] tracking-[0.2em] uppercase font-bold">Lyrics (optional)</p>
              <button type="button" onClick={writeLyrics} disabled={busy !== "" || !meta?.lyrics_ai} data-testid="aisong-write-lyrics" className="text-papaya text-xs font-bold flex items-center gap-1 disabled:opacity-40"><PencilSimple size={12} weight="fill" /> {busy === "lyrics" ? "Writing…" : "Write lyrics with AI"}</button>
            </div>
            <input value={form.topic} onChange={(e) => set("topic", e.target.value.slice(0, 300))} placeholder="What should the song be about? (for the AI lyricist)" data-testid="aisong-topic" className={`${inp} mb-2`} />
            <textarea rows={5} value={form.lyrics} onChange={(e) => set("lyrics", e.target.value.slice(0, 2500))} placeholder="[Verse 1]&#10;…&#10;[Chorus]&#10;…" data-testid="aisong-lyrics" className={inp} />
          </div>
        )}
        <button onClick={generate} disabled={busy !== "" || !meta?.enabled || left <= 0} data-testid="aisong-generate" className="w-full py-4 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-sm flex items-center justify-center gap-2 disabled:opacity-40">
          <Sparkle size={18} weight="fill" /> {busy === "generate" ? "Starting…" : "Generate song"}
        </button>
        <p className="text-cream/40 text-[11px] leading-relaxed flex gap-1.5"><Warning size={14} className="flex-shrink-0 mt-0.5" /> Songs are generated by ElevenLabs Music under Giffwurldaunce's licence for use inside the app. Don't submit copyrighted lyrics or imitate real artists. AI songs are labelled “AI song” when posted.</p>
      </section>

      <section className="mt-8">
        <p className="text-cream/50 text-xs tracking-[0.25em] uppercase font-bold mb-2">My songs</p>
        {songs.length === 0 ? <p className="text-cream/40 text-sm">Nothing yet — compose your first track above.</p> : (
          <div className="space-y-2" data-testid="aisong-library">
            {songs.map((s) => (
              <div key={s.id} className="p-3 rounded-2xl bg-white/5 border border-white/5" data-testid={`aisong-item-${s.id}`}>
                <div className="flex items-center gap-3">
                  <button onClick={() => s.status === "ready" && play(s)} disabled={s.status !== "ready"} data-testid={`aisong-play-${s.id}`} className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${playing === s.id ? "bg-papaya text-ink" : "bg-white/10 text-cream"} disabled:opacity-40`}>
                    {s.status === "generating" ? <div className="w-4 h-4 rounded-full border-2 border-papaya border-t-transparent animate-spin" /> : playing === s.id ? <Pause size={16} weight="fill" /> : <Play size={16} weight="fill" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-cream font-bold text-sm truncate">{s.title}</p>
                    <p className="text-cream/50 text-[11px] truncate">{s.style} · {s.mood} · {s.tempo} · {s.vocal} · {s.duration_sec}s</p>
                    <p className={`text-[10px] font-black tracking-[0.15em] uppercase mt-0.5 ${s.status === "ready" ? "text-emerald-300" : s.status === "failed" ? "text-red-300" : "text-sky-300"}`} data-testid={`aisong-status-${s.id}`}>{s.status === "generating" ? "Composing…" : s.status}{s.sound_id ? " · in music picker" : ""}</p>
                  </div>
                  <button onClick={() => remove(s)} data-testid={`aisong-delete-${s.id}`} className="w-9 h-9 rounded-full bg-red-500/15 text-red-300 flex items-center justify-center"><TrashSimple size={14} weight="bold" /></button>
                </div>
                {s.status === "failed" && <p className="text-red-300 text-[11px] mt-2 leading-relaxed" data-testid={`aisong-error-${s.id}`}>{s.error}</p>}
                {s.status === "ready" && (
                  <div className="flex gap-1.5 mt-2.5 flex-wrap">
                    <button onClick={() => publish(s)} data-testid={`aisong-publish-${s.id}`} className="px-3 py-1.5 rounded-full bg-white/5 border border-white/15 text-cream text-[10px] font-black tracking-[0.15em] uppercase flex items-center gap-1"><ShareNetwork size={11} weight="fill" /> {s.sound_id ? "In picker" : "Add to Music picker"}</button>
                    <button onClick={() => makeVideo(s)} data-testid={`aisong-video-${s.id}`} className="px-3 py-1.5 rounded-full bg-papaya text-ink text-[10px] font-black tracking-[0.15em] uppercase flex items-center gap-1"><FilmSlate size={11} weight="fill" /> Make a video</button>
                    <Link to="/music" className="px-3 py-1.5 rounded-full bg-white/5 border border-white/15 text-cream text-[10px] font-black tracking-[0.15em] uppercase flex items-center gap-1"><MusicNotes size={11} weight="fill" /> Library</Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
